import React from "react";
import { motion } from "framer-motion";
import TypingText from "./components/TypingText";

export default function TransformationView({
  title,
  input,
  process,
  output,
  explanation,
  explanationTitle = "Technical Breakdown",
  interactiveContent
}: any) {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 1000);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1000);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div style={styles.wrapper}>
      <h1 style={styles.title}>{title}</h1>

      {interactiveContent}

      <div style={{
        ...styles.container,
        flexDirection: isMobile ? "column" : "row"
      }}>
        {/* INPUT */}
        <div style={styles.panel}>
          <h3 style={{ margin: "0 0 8px 0", fontSize: "clamp(14px, 1.5vw, 16px)", color: "#E6EDF3" }}>Input</h3>
          {React.isValidElement(input) ? input : (
            <div style={{ 
              fontSize: "clamp(10px, 1vw, 13px)",
              lineHeight: 1.4,
              fontFamily: "monospace",
              wordBreak: "break-all"
            }}>
              <DataInspector data={input} />
            </div>
          )}
        </div>

        {/* PROCESS / PIPELINE */}
        <div style={{
          ...styles.center,
          flexDirection: isMobile ? "column" : "column" // Keep column but children might change
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            margin: isMobile ? "10px 0" : "4px 0",
            transform: isMobile ? "rotate(90deg)" : "none"
          }}>
            <svg width="32" height="12" viewBox="0 0 32 12" fill="none" style={{ filter: "drop-shadow(0 0 8px rgba(56, 189, 248, 0.8))" }}>
              <motion.path 
                d="M0 6 L28 6 M24 2 L30 6 L24 10" 
                stroke="#38BDF8" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                initial={{ pathLength: 0, opacity: 0.2 }}
                animate={{ pathLength: [0, 1, 1], opacity: [0.2, 1, 0.2] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
              />
            </svg>
          </div>
          
          <motion.div 
            style={styles.process}
            animate={{ 
              boxShadow: ["0 0 5px rgba(56,189,248,0.2)", "0 0 15px rgba(56,189,248,0.6)", "0 0 5px rgba(56,189,248,0.2)"],
              borderColor: ["rgba(56,189,248,0.2)", "rgba(56,189,248,0.6)", "rgba(56,189,248,0.2)"]
            }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            {process}
          </motion.div>

          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            margin: isMobile ? "10px 0" : "4px 0",
            transform: isMobile ? "rotate(90deg)" : "none"
          }}>
            <svg width="32" height="12" viewBox="0 0 32 12" fill="none" style={{ filter: "drop-shadow(0 0 8px rgba(56, 189, 248, 0.8))" }}>
              <motion.path 
                d="M0 6 L28 6 M24 2 L30 6 L24 10" 
                stroke="#38BDF8" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                initial={{ pathLength: 0, opacity: 0.2 }}
                animate={{ pathLength: [0, 1, 1], opacity: [0.2, 1, 0.2] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "linear", delay: 0.2 }}
              />
            </svg>
          </div>
        </div>

        {/* OUTPUT */}
        <div style={styles.panel}>
          <h3 style={{ margin: "0 0 8px 0", fontSize: "clamp(14px, 1.5vw, 16px)", color: "#E6EDF3" }}>Output</h3>
          {React.isValidElement(output) ? output : (
            <div style={{ 
              fontSize: "clamp(10px, 1vw, 13px)",
              lineHeight: 1.4,
              fontFamily: "monospace",
              wordBreak: "break-all"
            }}>
              <DataInspector data={output} />
            </div>
          )}
        </div>
      </div>

      {explanation && (
        <div style={styles.explanationBox}>
          <div style={styles.accentLine} />
          {explanationTitle && (
            <h4 style={styles.explanationTitle}>
              <span style={{ color: "#a855f7", fontSize: "1.2em", textShadow: "0 0 10px rgba(168, 85, 247, 0.8)" }}>✦</span> 
              {explanationTitle}
            </h4>
          )}
          <TypingText text={explanation} style={styles.explanationText} />
        </div>
      )}
    </div>
  );
}

export function DataInspector({ data, isRoot = true }: { data: any, isRoot?: boolean }) {
  if (!data) return <span style={{ color: "#64748B" }}>—</span>;
  
  if (typeof data !== "object") {
    if (isRoot && typeof data === "string") {
      return (
        <div style={{ 
          color: "#94A3B8", 
          fontFamily: "monospace", 
          opacity: 0.8, 
          lineHeight: 1.4, 
          wordBreak: "break-all", 
          padding: "8px 12px", 
          background: "rgba(255,255,255,0.02)", 
          borderRadius: 6 
        }}>
          {data}
        </div>
      );
    }
    
    let color = "#4ADE80"; // string (softer green)
    if (typeof data === "number") color = "#38BDF8"; // blue
    if (typeof data === "boolean") color = "#FBBF24"; // yellow
    
    return <span style={{ color, fontFamily: "monospace", fontSize: "11px", wordBreak: "break-all" }}>{String(data)}</span>;
  }

  return (
    <div style={{ 
      display: "flex", 
      flexDirection: "column", 
      width: "100%",
      paddingLeft: isRoot ? 0 : 8,
      borderLeft: isRoot ? "none" : "1px solid rgba(56,189,248,0.15)",
      marginTop: isRoot ? 0 : 4,
      gap: 1
    }}>
      {Object.entries(data).map(([key, value]) => {
        const isObject = value && typeof value === "object" && Object.keys(value).length > 0;
        
        return (
          <div key={key} style={{ 
            display: "flex", 
            flexDirection: isObject ? "column" : "row",
            alignItems: isObject ? "flex-start" : "center",
            justifyContent: isObject ? "flex-start" : "space-between",
            background: isRoot ? "rgba(255,255,255,0.015)" : "transparent",
            padding: isRoot ? "4px 8px" : "1px 0",
            borderRadius: isRoot ? 4 : 0,
            borderBottom: isRoot ? "1px solid rgba(255,255,255,0.02)" : "none"
          }}>
            <span style={{ 
              color: "#94A3B8", 
              fontSize: 11, 
              textTransform: "uppercase", 
              letterSpacing: 0.5,
              fontFamily: "monospace",
              marginBottom: isObject ? 2 : 0,
              minWidth: "max-content",
              marginRight: 12
            }}>
              {key.replace(/_/g, " ")}
            </span>
            
            {isObject ? (
              <DataInspector data={value} isRoot={false} />
            ) : (
              <div style={{ textAlign: "right", maxWidth: "70%" }}>
                <DataInspector data={value} isRoot={false} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const styles: any = {
  wrapper: {
    width: "100%",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
  },
  title: {
    marginBottom: "clamp(15px, 3vh, 30px)",
    fontSize: "clamp(20px, 3vw, 28px)",
    color: "#E6EDF3"
  },
  container: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
    gap: "clamp(10px, 2vw, 20px)",
    padding: "0 20px",
    width: "100%"
  },
  panel: {
    width: "100%",
    maxWidth: 450,
    minWidth: 300,
    padding: "clamp(12px, 2vh, 20px)",
    borderRadius: 12,
    background: "rgba(0,0,0,0.7)",
    border: "1px solid rgba(56,189,248,0.3)",
    textAlign: "left",
    position: "relative",
    overflow: "visible"
  },
  center: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "clamp(4px, 1vh, 10px)"
  },
  process: {
    color: "#E0F2FE",
    textShadow: "0 0 10px rgba(56, 189, 248, 0.8)",
    fontWeight: 700,
    fontFamily: "'Share Tech Mono', monospace",
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    padding: "10px 20px",
    background: "linear-gradient(135deg, rgba(15,23,42,0.9), rgba(56,189,248,0.15))",
    borderRadius: 8,
    border: "1px solid rgba(56,189,248,0.4)",
    fontSize: "clamp(11px, 1.1vw, 13px)",
    backdropFilter: "blur(10px)",
    textAlign: "center"
  },
  arrow: {
    fontSize: "clamp(18px, 2vw, 24px)",
    color: "#38BDF8"
  },
  explanationBox: {
    marginTop: "clamp(20px, 4vh, 40px)",
    padding: "clamp(16px, 2.5vh, 24px) clamp(20px, 3vw, 32px)",
    width: "100%",
    maxWidth: 850,
    background: "rgba(0, 0, 0, 0.6)",
    backdropFilter: "blur(8px)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: 8,
    textAlign: "left",
    position: "relative",
    overflow: "hidden"
  },
  accentLine: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 3,
    background: "#38BDF8",
    boxShadow: "0 0 10px rgba(56, 189, 248, 0.4)"
  },
  explanationTitle: {
    color: "#94A3B8",
    fontSize: 11,
    fontFamily: "monospace",
    marginBottom: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 1,
    display: "flex",
    alignItems: "center",
    gap: 8
  },
  explanationText: {
    color: "#E6EDF3",
    fontSize: 12,
    fontFamily: "monospace",
    lineHeight: 1.6,
    textAlign: "left",
    whiteSpace: "pre-wrap",
    fontWeight: 400
  }
};